import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, editionLabel, selectCls, btnBrand } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

const METHODS = [
  { key: "cash", label: "Efectivo" }, { key: "card", label: "Tarjeta (lector)" }, { key: "zelle", label: "Zelle" },
  { key: "cherry", label: "Cherry" }, { key: "other", label: "Otro" },
];

// Fix V (2026-10-13): cobrar en la caja: buscar al alumno, elegir la inscripcion y el medio.
export default function CashChargeModal({ open, onOpenChange, onCharged }) {
  const [q, setQ] = useState("");
  const [students, setStudents] = useState([]);
  const [student, setStudent] = useState(null);
  const [enrollmentId, setEnrollmentId] = useState("");
  const [form, setForm] = useState({ amount: "", method: "cash", is_reservation: false, notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setQ(""); setStudents([]); setStudent(null); setEnrollmentId(""); setError("");
    setForm({ amount: "", method: "cash", is_reservation: false, notes: "" });
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setStudents([]); return undefined; }
    const t = setTimeout(() => {
      api.get("/academy/students", { params: { q: q.trim() } }).then(({ data }) => setStudents(data.slice(0, 8))).catch(() => setStudents([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const pick = async (s) => {
    try {
      const { data } = await api.get(`/academy/students/${s.id}`);
      setStudent(data);
      const active = data.enrollments.filter((e) => !["cancelled", "expired", "transferred"].includes(e.status));
      const first = active.find((e) => e.account.balance > 0) || active[0];
      if (first) {
        setEnrollmentId(first.id);
        setForm((f) => ({ ...f, amount: String(first.steps.reservation ? first.account.balance : first.reservation_amount), is_reservation: !first.steps.reservation }));
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const active = (student?.enrollments || []).filter((e) => !["cancelled", "expired", "transferred"].includes(e.status));
  const enrollment = active.find((e) => e.id === enrollmentId);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!enrollment || !(Number(form.amount) > 0)) {
      setError("Elegí la inscripción y el monto");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.post(`/academy/enrollments/${enrollment.id}/payments`, {
        amount: Number(form.amount), method: form.method, is_reservation: form.is_reservation, notes: form.notes.trim() || null,
      });
      onCharged();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={ACADEMY.chargeModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Cobrar</DialogTitle>
          <DialogDescription>Queda registrado en la caja y en la cuenta del alumno.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {!student ? (
            <div className="space-y-2">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar alumno por nombre, email o teléfono" autoFocus data-testid={ACADEMY.chargeSearchInput} />
              {students.map((s) => (
                <button key={s.id} type="button" onClick={() => pick(s)} className="w-full text-left rounded-lg border border-border px-3 py-2 hover:border-brand" data-testid={`${ACADEMY.chargeStudentPrefix}${s.id}`}>
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.phone} · saldo {money(s.balance)}</p>
                </button>
              ))}
              {q.trim().length >= 2 && students.length === 0 && <p className="text-sm text-muted-foreground">No se encontró.</p>}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="font-medium">{student.name}</p>
                <Button variant="ghost" size="sm" onClick={() => setStudent(null)}>Cambiar</Button>
              </div>
              {active.length === 0 ? <p className="text-sm text-muted-foreground">No tiene inscripciones activas.</p> : (
                <div>
                  <Label className="text-sm text-muted-foreground mb-1 block">Inscripción</Label>
                  <select className={selectCls} value={enrollmentId} onChange={(e) => setEnrollmentId(e.target.value)} data-testid={ACADEMY.chargeEnrollmentSelect}>
                    {active.map((e) => <option key={e.id} value={e.id}>{e.course_name} · {editionLabel(e.edition)} · saldo {money(e.account.balance)}</option>)}
                  </select>
                </div>
              )}
              {enrollment && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-muted-foreground mb-1 block">Monto (USD)</Label>
                      <Input type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} data-testid={ACADEMY.chargeAmountInput} />
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground mb-1 block">Medio</Label>
                      <select className={selectCls} value={form.method} onChange={set("method")} data-testid={ACADEMY.chargeMethodSelect}>
                        {METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.is_reservation} onChange={(e) => setForm((f) => ({ ...f, is_reservation: e.target.checked }))} /> Es el pago de la reserva
                  </label>
                  <Input value={form.notes} onChange={set("notes")} placeholder="Nota (opcional)" />
                </>
              )}
            </>
          )}
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} disabled={saving || !enrollment} data-testid={ACADEMY.chargeSaveButton}>{saving ? "Cobrando..." : "Cobrar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
